import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Users, Bed, Maximize2, Wifi, Tv, Coffee, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { rooms } from '../data/mock';

const Rooms = () => {
  return (
    <>
      <Helmet>
        <title>{`Rooms & Tariff | Hotel Suvidha Deluxe Haridwar`}</title>
        <meta name="description" content="Explore affordable rooms and tariff at Hotel Suvidha Deluxe Haridwar with modern amenities." />
      </Helmet>
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-serif text-navy-900">Rooms & Tariff</h1>
          <p className="text-xl text-gold-400 max-w-3xl mx-auto leading-relaxed">
            Choose from our selection of beautifully designed rooms, each crafted for comfort and elegance
          </p>
        </div>
      </section>

      {/* Rooms Section */}
      <section className="container mx-auto px-4 -mt-12">
        <div className="space-y-12">
          {rooms.map((room, index) => (
            <Card key={room.id} className={`border-0 shadow-2xl overflow-hidden ${index % 2 === 0 ? '' : ''}`}>
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                {/* Image */}
                <div className={`relative h-96 lg:h-auto overflow-hidden ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <img
                    src={room.image}
                    alt={room.name}
                    loading="lazy"
                    width={800}
                    height={400}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-6 right-6 bg-gold-600 text-white px-6 py-3 rounded-full shadow-lg">
                    <span className="text-2xl font-bold">₹{room.price}</span>
                    <span className="text-sm ml-1">/night</span>
                  </div>
                </div>

                {/* Content */}
                <CardContent className={`p-8 lg:p-12 flex flex-col justify-center ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <h2 className="text-4xl font-bold text-navy-900 mb-4 font-serif">{room.name}</h2>
                  <p className="text-gray-600 text-lg mb-6 leading-relaxed">{room.description}</p>

                  {/* Room Details */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      <Users className="text-gold-600" size={24} />
                      <div>
                        <p className="text-xs text-gray-500">Capacity</p>
                        <p className="font-semibold text-navy-900">{room.capacity}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Maximize2 className="text-gold-600" size={24} />
                      <div>
                        <p className="text-xs text-gray-500">Room Size</p>
                        <p className="font-semibold text-navy-900">{room.size}</p>
                      </div>
                    </div>
                  </div>

                  {/* Amenities */}
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-navy-900 mb-3">Room Amenities</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {room.amenities.map((amenity, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle2 className="text-gold-600 flex-shrink-0" size={18} />
                          <span className="text-gray-700">{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link to="/booking">
                    <Button size="lg" className="w-full bg-gold-600 hover:bg-gold-700 text-white text-lg py-6">
                      Book This Room
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing Note */}
      <section className="container mx-auto px-4 mt-16">
        <Card className="border-0 shadow-lg bg-gray-50">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold text-navy-900 mb-4">Pricing Information</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                <span>All prices are inclusive of taxes</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                <span>Complimentary breakfast included</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                <span>Special rates available for extended stays</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                <span>Extra person charges: ₹500 per night</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                <span>Check-in: 2:00 PM | Check-out: 11:00 AM</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* CTA Section */}
      <section className="py-20 mt-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 font-serif">Need Help Choosing?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Our team is here to help you find the perfect room for your stay
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gold-600 hover:bg-gold-700 text-white px-10 py-6 text-lg">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Rooms;
